import React, { createContext, useContext, useEffect, useState } from "react";
import ManagerApprovalModal from "./ManagerApprovalModal";
import DiscountApprovalModal from "./DiscountApprovalModal";
import ProductsPopup from "./ProductsPopup";
import CompanionPopup from "./CompanionPopup";
import db from "../../database";
import useAuthStore from "../../stores/useAuthStore";

import QRCodeModal from "./QRCodeModal";

// Event Bus Context
const EventBusContext = createContext();

// Event Bus Provider
const EventBusProvider = ({ children }) => {
  const [events, setEvents] = useState([]);
  const [isManagerApprovalModalVisible, setManagerApprovalModalVisible] = useState(false);
  const [approvalGrantedCallback, setApprovalGrantedCallback] = useState(null);

  const [isDiscountApproval, setIsDiscountApproval] = useState(false);
  const [discountApprovals, setDiscountApprovals] = useState([]);

  const [selectedItems, setSelectedItems] = useState([]);
  const [visibleProductsModal, setVisibleProductsModal] = useState(false);
  const [productData, setProductData] = useState([]);
  const [itemSelectionCallback, setItemSelectionCallback] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState({});
  const [selectedProductCallback, setSelectedProductCallback] = useState(null);
  const [weightPopup, setWeightPopup] = useState(false);
  const [productModalVisible, setProductModalVisible] = useState(false);
  const [inputFocused, setInputFocused] = useState(null);
  const [selectedProductInCart, setSelectedProductInCart] = useState({});
  const [popupKey, setPopupKey] = useState(0);
  const [isQRCodeModalVisible, setQRCodeModalVisible] = useState(false);
  const [qrCodeData, setQRCodeData] = useState(null);
  const [qrPaymentCallback, setQRPaymentCallback] = useState(null);
  const [qrPaymentMethod, setQRPaymentMethod] = useState(null);
  const [selectedCoupon, setSelectedCoupon] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [discountAmt, setDiscountAmt] = useState(0);
  const [totalSTD, setTotalSTD] = useState(0);
  const [popupCart, setPopupCart] = useState(null);
  const [popupSetCart, setPopupSetCart] = useState(() => {});
  const [popupSetProductStock, setPopupSetProductStock] = useState(() => {});

  const [companionPopupVisible, setCompanionPopupVisible] = useState(false);
  const [companionPopupProduct, setCompanionPopupProduct] = useState(null);
  const [companionAddCallback, setCompanionAddCallback] = useState(null);

  const tillData = useAuthStore.getState().tillData;
  const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;

  const dispatchEvent = (eventName, ruleName, ruleCondition, approvers, data, callback) => {
    const eventData = WorkflowEngine.processEvent({ name: eventName, ruleName, ruleCondition, data });
    const filteredApprovals = approvers.filter((res) => res.csBunitId === bunitId);
    setEvents([{ eventName, ruleName, ruleCondition, filteredApprovals }]);
    if (filteredApprovals.length > 0 && eventData?.managerApprovalRequired) {
      openManagerApprovalModalIfNeeded(eventData, callback);
    } else {
      callback({ approvalGranted: true });
    }
  };

  const discountApproval = (approvals, pricingRule, couponCode, discount, totalStd, callback) => {
    setEvents([{ approvals, callback }]);
    setSelectedCoupon(pricingRule);
    setCouponCode(couponCode);
    setDiscountAmt(discount);
    setTotalSTD(totalStd);
    setIsDiscountApproval(true);
    setDiscountApprovals(() => callback);
  };

  const dispatchQRCodeEvent = (qrCodeResponse, paymentMethod, callback) => {
    setQRCodeData(qrCodeResponse);
    setQRPaymentMethod(paymentMethod);
    setQRCodeModalVisible(true);
    setQRPaymentCallback(() => callback);
  };

  const openManagerApprovalModalIfNeeded = (eventData, callback) => {
    if (eventData && eventData.managerApprovalRequired) {
      localStorage.setItem("approvalFlag", "0");
      setManagerApprovalModalVisible(true); // Open manager approval modal
      setApprovalGrantedCallback(() => callback);
    } else if (eventData && !eventData.managerApprovalRequired) {
    }
  };

  // Function to close the manager approval modal
  const closeManagerApprovalModal = () => {
    localStorage.setItem("approvalFlag", "1");
    setManagerApprovalModalVisible(false);
  };

  const handleApprovalGranted = (data) => {
    closeManagerApprovalModal();
    // Simulate approval
    const approvalGranted = data;
    const eventData = { approvalGranted };
    if (approvalGranted && approvalGrantedCallback) {
      approvalGrantedCallback(eventData);
    }
  };

  const handleDiscountApproval = (data) => {
    closeDiscountApproval();
    const approvalGranted = data;
    const eventData = { approvalGranted };
    discountApprovals(eventData);
  };

  const closeDiscountApproval = () => {
    setIsDiscountApproval(false);
  };

  const closeQRCodeModal = () => {
    setQRCodeModalVisible(false);
    setQRPaymentMethod(null);
    setQRCodeData(null);
    qrPaymentCallback(null);
  };

  const dispatchItemSelection = async (selecTedItem, cart, setCart, setProductStock, setShowCompleteSaleStock, setCompleteSaleStockData, callback) => {
    if (selecTedItem?.companion === "Y") {
      setCompanionPopupProduct(selecTedItem);
      setCompanionAddCallback(() => callback);
      setPopupCart(cart);
      setPopupSetCart(() => setCart);
      setPopupSetProductStock(() => setProductStock);
      setCompanionPopupVisible(true);
    } else {
      const items = selecTedItem?.childItems || [];
      setSelectedProduct(selecTedItem);
      setVisibleProductsModal(true);
      setSelectedItems(items);
      setProductModalVisible(true);
      setItemSelectionCallback(() => callback);

      setPopupCart(cart);
      setPopupSetCart(() => setCart);
      setPopupSetProductStock(() => setProductStock);

      // SQLite/CW Browser: .filter() on DB collections can't carry closures over IPC.
      // Always toArray() first and filter in plain JS.
      const allProducts = await db.products.toArray();
      const productsList = allProducts.filter((product) => product.isactive == true && product.productSegment !== "BG");

      // Separate matched and unmatched products
      const matchedProducts = [];
      const remainingProducts = [];

      productsList.forEach((item) => {
        const selectedItem = items.find((selected) => selected.productId === item.productId);
        if (selectedItem) {
          matchedProducts.push({ ...item, weight: selectedItem.weight });
        } else {
          remainingProducts.push(item);
        }
      });

      // Combine matched products first, then the remaining products
      const updatedProductsList = [...matchedProducts, ...remainingProducts];

      setProductData(updatedProductsList);
    }
  };

  const onSubmit = () => {
    const eventData = { selectedItems };
    itemSelectionCallback(eventData);
  };

  const dispatchWeight = (product, setInputFocused, selectedProductInCart, setSelectedProductInCart, callback) => {
    setPopupKey((k) => k + 1); // Force fresh ProductsPopup mount — prevents stale UOM state
    setSelectedProduct(product);
    const hasAltUom = product?.alternateUom?.length > 0 && Array.isArray(product?.alternateUom) && product?.showAlternateUomInPos === "Y";
    if (!hasAltUom) {
      setWeightPopup(true);
    }
    setInputFocused(() => setInputFocused);
    setProductModalVisible(true);
    setSelectedProductCallback(() => callback);
    setSelectedProductInCart(() => callback);
  };

  const closeCompanionPopup = () => {
    setCompanionPopupVisible(false);
    setCompanionPopupProduct(null);
    setCompanionAddCallback(null);
  };

  const handleCompanionAdd = (headerProduct, companionProduct) => {
    if (!companionAddCallback) return;
    const selectedItems = companionProduct ? [companionProduct] : [];
    selectedItems[0].name = companionProduct?.companion_product_name;
    selectedItems[0].productId = companionProduct?.companion_product_id;
    selectedItems[0].lineId = companionProduct?.line_id;
    selectedItems[0].weight = 0;
    selectedItems[0].discount = 0;
    selectedItems[0].taxAmount = 0;
    selectedItems[0].realPrice = 0;
    selectedItems[0].nettotal = 0;
    companionAddCallback({ selectedItems });
    closeCompanionPopup();
  };

  return (
    <EventBusContext.Provider value={{ dispatchEvent, events, discountApproval, dispatchItemSelection, dispatchQRCodeEvent, closeQRCodeModal, dispatchWeight }}>
      {children}
      <CompanionPopup visible={companionPopupVisible} onClose={closeCompanionPopup} selectedProduct={companionPopupProduct} onAddToCart={handleCompanionAdd} />
      {isManagerApprovalModalVisible && (
        <ManagerApprovalModal
          event={events}
          onClose={closeManagerApprovalModal}
          open={isManagerApprovalModalVisible}
          setManagerApprovalModalVisible={setManagerApprovalModalVisible}
          onApprovalGranted={handleApprovalGranted}
        />
      )}
      {isDiscountApproval && (
        <DiscountApprovalModal
          event={events}
          onClose={closeDiscountApproval}
          open={isDiscountApproval}
          setIsDiscountApproval={setIsDiscountApproval}
          onApprovalGranted={handleDiscountApproval}
          selectedCoupon={selectedCoupon}
          setSelectedCoupon={setSelectedCoupon}
          couponCode={couponCode}
          discountAmt={discountAmt}
          totalSTD={totalSTD}
        />
      )}
      {productModalVisible && (
        <ProductsPopup
          key={popupKey}
          productData={productData}
          event={events}
          visibleProductsModal={visibleProductsModal}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          dispatchItemSelection={dispatchItemSelection}
          setVisibleProductsModal={setVisibleProductsModal}
          onSubmit={onSubmit}
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          selectedProductCallback={selectedProductCallback}
          weightPopup={weightPopup}
          setWeightPopup={setWeightPopup}
          inputFocused={inputFocused}
          selectedProductInCart={selectedProductInCart}
          setSelectedProductInCart={setSelectedProductInCart}
          cart={popupCart}
          setCart={popupSetCart}
          setProductStock={popupSetProductStock}
        />
      )}
      {isQRCodeModalVisible && (
        <QRCodeModal
          qrCodeData={qrCodeData}
          paymentMethod={qrPaymentMethod}
          open={isQRCodeModalVisible}
          onClose={closeQRCodeModal}
          qrPaymentCallback={qrPaymentCallback}
          setQRCodeModalVisible={setQRCodeModalVisible}
        />
      )}
    </EventBusContext.Provider>
  );
};

// Use this hook to access the event bus
const useEventBus = () => useContext(EventBusContext);

// Workflow Engine
const WorkflowEngine = {
  processEvent: (eventWithRules) => {
    // Prefer the canonical eventName — tenants ship arbitrary descriptive
    // ruleNames ("Manager OK for returns", "Allow without PAN", …) that don't
    // match any case in the switch below, so the original `rule = ruleName`
    // line silently dropped through to default and returned approvalGranted:
    // true for every workflow rule whose ruleName wasn't literally one of the
    // case strings. Match on eventName first; fall back to ruleName for
    // backward compatibility with older configs that DID set ruleName to the
    // event identifier. (Tab8 issue #5 — Sales Return Approval popup still
    // missing after the Tab7 fix because that fix only added case strings,
    // not the eventName lookup.)
    const rule = eventWithRules.ruleName;
    const data = eventWithRules.data;
    let actions;
    try {
      actions = JSON.parse(eventWithRules.ruleCondition).conditions[0].actions;
    } catch (err) {
      console.warn("[WorkflowEngine] invalid ruleCondition:", err?.message);
      return { approvalGranted: true };
    }
    // Each case returns explicitly. Adding `break` after every if so that
    // falling through to the next case (which previously matched the wrong
    // actionType and silently swallowed the event) can't happen anymore. This
    // was the cause of the Sales Return Approval popup never appearing when
    // ruleName matched but actionType was named slightly differently. (Issue #13)
    switch (rule) {
      case "Draft Print Receipt Approval":
        if (actions[0]?.actionType === "requestApprovalForDraftPrint") {
          localStorage.setItem("approvalFlag", "0");
          return { managerApprovalRequired: true, actions };
        }
        break;
      case "Sales Return Approval":
      case "Sales Return Process":
        if (actions[0]?.actionType === "requestApprovalForReturn" || actions[0]?.actionType === "requestApprovalForSalesReturn") {
          return { managerApprovalRequired: true, actions };
        }
        // Fall back to required when ruleName matches but actionType is unrecognised
        // — most return rules ARE meant to require approval, so failing open here
        // matches operator expectations better than silently passing.
        return { managerApprovalRequired: true, actions };
      case "Cancel KOT":
        if (actions[0]?.actionType === "requestApprovalForCancelKOT") {
          return { managerApprovalRequired: true, actions };
        }
        break;
      case "Coupon Approval":
        if (actions[0]?.actionType === "requestApprovalForCoupon") {
          return { managerApprovalRequired: true, actions };
        }
        break;
      case "Receipt Reprint Approval":
        if (actions[0]?.actionType === "requestApprovalForReprint") {
          return { managerApprovalRequired: true, actions };
        }
        break;
      case "Cancel Parked Bill Approval":
        if (actions[0]?.actionType === "requestApprovalForCancelParkedBill") {
          return { managerApprovalRequired: true, actions };
        }
        break;
      case "Cash Management Request": {
        let criteria;
        try {
          criteria = JSON.parse(eventWithRules?.ruleCondition)?.conditions[0].criteria;
        } catch {
          criteria = [];
        }
        if (actions[0]?.actionType === "requestApprovalForCashManagement" && Number(criteria?.[0]?.value) < Number(data?.amount)) {
          return { managerApprovalRequired: true, actions };
        }
        return { managerApprovalRequired: false, actions };
      }
      default:
        return { approvalGranted: true };
    }
    return { approvalGranted: true };
  },
};

export { EventBusProvider, useEventBus };
